/**
 * Order Row Component
 */

import { useState, useCallback, useMemo, memo } from 'react';
import { useSelector } from '@spcc/core';
import { selectUserRoleType } from '@spcc/domains/auth';
import {
  TableRow,
  TableCell,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TenantCell,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  cn,
} from '@spcc/uikit';
import {
  MoreHorizontal,
  Eye,
  Trash2,
  Download,
  Factory,
  Copy,
  Check,
  Package,
  Truck,
} from '@spcc/uikit';
import { useTranslation, getLocalizedValue } from '@spcc/core';
import { getOrderStatusConfig, getProductColorStyles } from '../constants';
import {
  getStatusTranslationKey,
  stopPropagation,
  formatProcessingToDeliveredDuration,
  formatLoadingDuration,
  getLocalizedQuantityUnit,
} from '../utils';
import type { OrderRowProps } from '../types';
import type { OrderTableColumnKey } from '../types';

function getVisibleColumnSet(
  visibleColumns: OrderTableColumnKey[],
): Set<OrderTableColumnKey> {
  return new Set(visibleColumns);
}

function normalizeDisplayText(value: string | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getOrderDriverDisplay(order: OrderRowProps['order']): {
  driverName: string | null;
  vehicleLicensePlate: string | null;
} {
  return {
    driverName: normalizeDisplayText(order.driverName),
    vehicleLicensePlate: normalizeDisplayText(order.vehicleLicensePlate),
  };
}

const PRODUCT_EXCLUDED_WORDS = new Set(['paper', 'bag', 'plastic', 'bulk'])

function abbreviateProductName(name: string): string {
  if (!name || name === '—') return name
  const words = name.trim().split(/\s+/)
  const significant = words.filter(w => !PRODUCT_EXCLUDED_WORDS.has(w.toLowerCase()))
  if (significant.length === 0) return name
  return significant.map(w => w.charAt(0).toUpperCase()).join('')
}

function canCancelOrder(order: OrderRowProps['order'], roleType?: string): boolean {
  if (roleType === 'packhouse_manager') return false;
  const sapOrderNumber = order.sapSalesOrderNumber?.trim();
  return order.status === 'pending' && !sapOrderNumber;
}

function OrderRowComponent({
  order,
  isSelected,
  isRTL,
  showTenantColumn,
  visibleColumns,
  onClick,
  onDoubleClick,
  onCancel,
  onDownloadPDF,
  isChecked = false,
  onCheckChange,
  showCheckbox = false,
  roleType,
  visibleColumnSet,
}: OrderRowProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const config = getOrderStatusConfig(order.status);
  const StatusIcon = config.icon;
  const product = order.items[0];
  const driverDisplay = getOrderDriverDisplay(order);
  const processingToDeliveredDuration = formatProcessingToDeliveredDuration(
    order.processingToDeliveredSeconds,
  );

  const handleCopyOrderNumber = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const text = order.orderNumber?.trim();
      if (!text) return;
      navigator.clipboard.writeText(text).then(
        () => {
          setCopied(true);
          toast({
            title: t('orders.orderNumberCopied'),
            description: text,
          });
          setTimeout(() => setCopied(false), 2000);
        },
        () => {},
      );
    },
    [order.orderNumber, t],
  );

  return (
    <TableRow
      data-testid={`order-row-${order.id}`}
      className={cn(
        'border-border/30 cursor-pointer group transition-colors duration-150',
        isSelected
          ? 'bg-primary/8 hover:bg-primary/12 ring-1 ring-inset ring-primary/20'
          : 'hover:bg-muted/50',
      )}
      onClick={() => onClick(order)}
      onDoubleClick={() => onDoubleClick(order)}
    >
      {showCheckbox && (
        <TableCell className="px-3 py-4 w-[50px]">
          <Checkbox
            checked={isChecked}
            onCheckedChange={(checked) => {
              onCheckChange?.(order.id, checked === true);
            }}
            onClick={(event) => event.stopPropagation()}
            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
          />
        </TableCell>
      )}
      {visibleColumnSet.has('orderNumber') && (
        <TableCell className="px-5 py-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 -ms-1.5 text-start',
                  'font-semibold text-foreground text-sm hover:bg-muted/80 transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1',
                )}
                onClick={handleCopyOrderNumber}
              >
                <span
                  className={cn('w-2 h-2 rounded-full shrink-0', config.dotColor)}
                  aria-hidden
                />
                <span>{order.orderNumber}</span>
                {copied ? (
                  <Check
                    className="w-3.5 h-3.5 text-emerald-600 shrink-0"
                    aria-hidden
                  />
                ) : (
                  <Copy
                    className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 transition-opacity"
                    aria-hidden
                  />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('orders.copyOrderNumber')}</TooltipContent>
          </Tooltip>
        </TableCell>
      )}
      {visibleColumnSet.has('sapSalesOrderNumber') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm font-mono">
            {order.sapSalesOrderNumber ?? '—'}
          </span>
        </TableCell>
      )}
      {showTenantColumn && visibleColumnSet.has('tenant') && (
        <TableCell className="px-5 py-4">
          <TenantCell tenant={order.tenant} />
        </TableCell>
      )}
      {visibleColumnSet.has('factory') && (
        <TableCell className="px-5 py-4">
          <div className={cn('flex items-center gap-2')}>
            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Factory className="w-4 h-4 text-slate-500" />
            </div>
            <span className="text-sm text-foreground font-medium truncate max-w-[120px]">
              {order.factoryName || '—'}
            </span>
          </div>
        </TableCell>
      )}
      {visibleColumnSet.has('product') && (
        <TableCell className="px-5 py-4">
          {(() => {
            const label =
              getLocalizedValue(product?.product, isRTL ? 'ar' : 'en') || '—';
            const displayLabel = isRTL ? label : abbreviateProductName(label);
            const colors = getProductColorStyles(label);
            const isBulk =
              product?.quantity?.unit === 'tons' ||
              product?.quantity?.unit === 'bulk';
            const PackageIcon = isBulk ? Truck : Package;

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm cursor-default',
                    )}
                    style={{ backgroundColor: colors.bg, color: colors.text }}
                  >
                    <span
                      className="w-2 h-2 rounded-full ring-1 ring-white/50"
                      style={{ backgroundColor: colors.dot }}
                    />
                    <span>{displayLabel}</span>
                    <PackageIcon className="w-3 h-3 opacity-70" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            );
          })()}
        </TableCell>
      )}
      {visibleColumnSet.has('driver') && (
        <TableCell className="px-5 py-4">
          {driverDisplay.driverName || driverDisplay.vehicleLicensePlate ? (
            <div className="flex flex-col">
              {driverDisplay.driverName && (
                <span className="text-foreground text-sm font-medium">
                  {driverDisplay.driverName}
                </span>
              )}
              {driverDisplay.vehicleLicensePlate && (
                <span className="text-muted-foreground text-xs font-mono tracking-wide">
                  {driverDisplay.vehicleLicensePlate}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
      )}
      {visibleColumnSet.has('quantity') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm">
            {product?.quantity.value}{' '}
            {getLocalizedQuantityUnit(product?.quantity.unit, t)}
          </span>
        </TableCell>
      )}
      {visibleColumnSet.has('orderDate') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm">
            {order.orderDate}
          </span>
        </TableCell>
      )}
      {visibleColumnSet.has('deliveryDate') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm">
            {order.deliveryDate}
          </span>
        </TableCell>
      )}
      {visibleColumnSet.has('processingToDeliveredTime') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm">
            {processingToDeliveredDuration}
          </span>
        </TableCell>
      )}
      {visibleColumnSet.has('loadingTime') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm">
            {formatLoadingDuration(order.loadingDurationSeconds)}
          </span>
        </TableCell>
      )}
      {visibleColumnSet.has('status') && (
        <TableCell
          data-testid={`order-status-${order.id}`}
          className="px-5 py-4"
        >
          <Badge
            className={cn(
              config.bg,
              config.text,
              'border-0 font-semibold px-3 py-1 shadow-sm flex items-center gap-1.5',
            )}
          >
            <StatusIcon className="w-3.5 h-3.5" />
            <span
              className={cn(
                'w-2 h-2 rounded-full animate-pulse',
                config.dotColor,
              )}
            />
            {t(`orders.${getStatusTranslationKey(order.status)}`)}
          </Badge>
        </TableCell>
      )}
      {visibleColumnSet.has('actions') && (
        <TableCell className="px-5 py-4 text-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={stopPropagation}>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl hover:bg-muted"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  stopPropagation(e);
                  onClick(order);
                }}
              >
                <Eye className="w-4 h-4 me-2" />
                {t('common.view')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  stopPropagation(e);
                  onDownloadPDF(order);
                }}
              >
                <Download className="w-4 h-4 me-2" />
                {t('orders.downloadPDF')}
              </DropdownMenuItem>
              {canCancelOrder(order, roleType) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      stopPropagation(e);
                      onCancel(order);
                    }}
                    variant="destructive"
                  >
                    <Trash2 className="w-4 h-4 me-2" />
                    {t('orders.cancel')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      )}
    </TableRow>
  );
}

export const OrderRow = memo(OrderRowComponent, (prev, next) => {
  return (
    prev.order.id === next.order.id &&
    prev.isSelected === next.isSelected &&
    prev.order.status === next.order.status &&
    prev.isRTL === next.isRTL &&
    prev.showTenantColumn === next.showTenantColumn &&
    prev.isChecked === next.isChecked &&
    prev.showCheckbox === next.showCheckbox &&
    prev.visibleColumns === next.visibleColumns &&
    prev.roleType === next.roleType &&
    prev.visibleColumnSet === next.visibleColumnSet
  );
});
